import * as React from 'react';
import { cn } from '@/lib/utils';

export interface HoverBreakdownRow {
  key?: string;
  label: string;
  value: string;
  color?: string;
  pct?: number | null;
}

export interface HoverBreakdownProps {
  heading: string;
  rows: readonly HoverBreakdownRow[];
  className?: string;
  caret?: number | 'center' | null;
  caretSide?: 'bottom' | 'top';
  style?: React.CSSProperties;
  panelRef?: React.Ref<HTMLElement>;
}

export const HOVER_PANEL_SURFACE =
  'block rounded-xl border border-white/10 bg-[#0F172A]/95 px-3 py-2.5 text-left text-white shadow-lg backdrop-blur-sm';

const HOVER_PANEL_BASE =
  'pointer-events-none absolute left-1/2 z-20 hidden w-max max-w-[240px] -translate-x-1/2 group-hover:block group-focus-visible:block';

export const HOVER_PANEL_ABOVE = `${HOVER_PANEL_BASE} bottom-full mb-1.5`;


export const HOVER_ANCHOR =
  'group relative cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand';

export const HOVER_PANEL_BELOW = `${HOVER_PANEL_BASE} top-full mt-1.5`;

export const CURSOR_PANEL_GAP_PX = 16;

export function useCursorPanel<T extends HTMLElement>(active: boolean) {
  const hostRef = React.useRef<T | null>(null);
  const panelRef = React.useRef<HTMLElement | null>(null);
  const [point, setPoint] = React.useState<{
    x: number;
    y: number;
    clientX: number;
    clientY: number;
  } | null>(null);
  const [size, setSize] = React.useState({ w: 200, h: 76 });

  React.useEffect(() => {
    const el = panelRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) setSize({ w: width, h: height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  });

  React.useEffect(() => {
    if (!active) setPoint(null);
  }, [active]);

  const onMouseMove = React.useCallback((e: React.MouseEvent) => {
    const host = hostRef.current;
    if (!host) return;
    const r = host.getBoundingClientRect();
    setPoint({
      x: e.clientX - r.left,
      y: e.clientY - r.top,
      clientX: e.clientX,
      clientY: e.clientY,
    });
  }, []);

  let style: React.CSSProperties | null = null;
  if (active && point) {
    const vw = typeof window === 'undefined' ? 1024 : window.innerWidth;
    const vh = typeof window === 'undefined' ? 768 : window.innerHeight;
    const EDGE = 8;

    const flip = point.clientX + CURSOR_PANEL_GAP_PX + size.w > vw - EDGE;
    const left = flip
      ? point.x - CURSOR_PANEL_GAP_PX - size.w
      : point.x + CURSOR_PANEL_GAP_PX;

    let top = point.y - size.h / 2;
    const topInViewport = point.clientY - size.h / 2;
    if (topInViewport < EDGE) top += EDGE - topInViewport;
    else if (topInViewport + size.h > vh - EDGE) top -= topInViewport + size.h - (vh - EDGE);

    style = { left, top };
  }

  return { hostRef, panelRef, onMouseMove, style };
}

export function HoverBreakdown({
  heading,
  rows,
  className,
  style,
  caret,
  caretSide = 'bottom',
  panelRef,
}: HoverBreakdownProps) {
  return (
    <span
      ref={panelRef}
      role="tooltip"
      className={cn(HOVER_PANEL_SURFACE, 'min-w-[168px]', className)}
      style={style}
    >
      {caret != null && (Number.isFinite(caret as number) || caret === 'center') && (
        <span
          aria-hidden
          className={cn(
            'absolute h-2.5 w-2.5 rounded-[1px] bg-[#0F172A]/95',
            caretSide === 'top' ? '-top-1 border-l border-t border-white/10' : '-bottom-1 border-b border-r border-white/10'
          )}
          style={
            caret === 'center'
              ? { left: '50%', transform: 'translateX(-50%) rotate(45deg)' }
              : { left: (caret as number) - 5, transform: 'rotate(45deg)' }
          }
        />
      )}

      <span className="mb-1.5 block text-[11px] font-medium text-[#94A3B8]">{heading}</span>

      <span className="block space-y-1.5">
        {rows.map((r, i) => (
          <span
            key={r.key ?? r.label ?? i}
            className="flex items-center justify-between gap-3 text-[12px]"
          >
            <span className="inline-flex min-w-0 items-center gap-1.5 text-[#E2E8F0]">
              {r.color && (
                <span
                  className="h-1.5 w-1.5 shrink-0 rounded-full"
                  style={{ background: r.color }}
                  aria-hidden
                />
              )}
              <span className="truncate">{r.label}</span>
            </span>
            <span className="shrink-0 text-right tabular-nums">
              <span className="font-semibold text-white">{r.value}</span>
              {r.pct != null && Number.isFinite(r.pct) && (
                <span className="ml-1.5 text-[#94A3B8]">{Math.round(r.pct)}%</span>
              )}
            </span>
          </span>
        ))}
      </span>

    </span>
  );
}
